import * as THREE from "three";
import {
  FLAP_IMPULSE,
  FLAP_STAMINA_COST,
  GRAVITY,
  PIGEON_HEIGHT,
  PIGEON_RADIUS,
  SPRINT_SPEED,
  STAMINA_MAX,
  STAMINA_RECOVERY_PER_SECOND,
  WALK_SPEED,
} from "@palomitas/shared";
import type { AABB } from "@palomitas/shared";
import type { InputFrame } from "../net/transport";

// Spawn at the south end of the center path, facing inward toward map center.
export const PIGEON_SPAWN = new THREE.Vector3(0, PIGEON_HEIGHT, 42);

const BABY_SCALE      = 0.55;
const SCALE_PER_LEVEL = 0.12;
/** How far the visual bodyGroup is pushed down so feet sit near the ground. */
const BODY_Y_OFFSET   = -0.13;
const HEAD_RADIUS     = 0.1;  // before bodyGroup scale
const HEAD_LOCAL_Y    =  0.21; // headGroup y in bodyGroup space
const HEAD_LOCAL_Z    = -0.24; // headGroup z in bodyGroup space

const BABY_BODY    = new THREE.Color(0xe8ecf0);
const ADULT_BODY   = new THREE.Color(0x8a96a8);
const BABY_COLLAR  = new THREE.Color(0xb8d4b0);
const ADULT_COLLAR = new THREE.Color(0x3a7a50);
const BABY_WING    = new THREE.Color(0xd0d8e0);
const ADULT_WING   = new THREE.Color(0x4d5b6e);
const BABY_TAIL    = new THREE.Color(0xc8d4dc);
const ADULT_TAIL   = new THREE.Color(0x5c6878);
const BABY_FEATH   = new THREE.Color(0xb0bcc8);
const ADULT_FEATH  = new THREE.Color(0x333d4d);
const BASE_AIRBORNE_HORIZONTAL_SPEED_MULTIPLIER = 1;
const AIRBORNE_HORIZONTAL_SPEED_MULTIPLIER_PER_LEVEL = 0.5;

export class Pigeon {
  readonly group    = new THREE.Group();
  readonly position = PIGEON_SPAWN.clone();
  readonly velocity = new THREE.Vector3();
  grounded   = true;
  stamina    = STAMINA_MAX;
  staminaMax = STAMINA_MAX;
  speedBonus = 0;
  airborneHorizontalSpeedMultiplier = BASE_AIRBORNE_HORIZONTAL_SPEED_MULTIPLIER;

  readonly viewBeak: THREE.Group;

  private readonly bodyGroup:       THREE.Group;
  private readonly headGroup:       THREE.Group;
  private readonly leftWingPivot:   THREE.Group;
  private readonly rightWingPivot:  THREE.Group;
  private readonly leftLegPivot:    THREE.Group;
  private readonly rightLegPivot:   THREE.Group;
  private wingPhase = 0;
  private peckTimer = 0;
  private static readonly PECK_DURATION = 0.18; // seconds for one peck cycle

  /** 0 = idle, peaks at 1 at the halfway point of the peck animation. */
  get peckProgress(): number {
    if (this.peckTimer <= 0) return 0;
    const t = 1 - this.peckTimer / Pigeon.PECK_DURATION;
    return t < 0.5 ? t * 2 : (1 - t) * 2;
  }

  private readonly matBody:   THREE.MeshLambertMaterial;
  private readonly matCollar: THREE.MeshLambertMaterial;
  private readonly matWing:   THREE.MeshLambertMaterial;
  private readonly matTail:   THREE.MeshLambertMaterial;
  private readonly matFeath:  THREE.MeshLambertMaterial;

  private static readonly HEAD_REST_Y =  HEAD_LOCAL_Y;
  private static readonly HEAD_REST_Z = HEAD_LOCAL_Z;
  /**
   * Baseline forward-down tilt of the headGroup. Real pigeons hold their
   * heads angled down while scavenging; it also pulls the beak clear of the
   * head silhouette so the TP camera can see it.
   */
  private static readonly HEAD_REST_TILT = -0.55;
  /** Hip Y position in bodyGroup space (top of leg cylinder). */
  private static readonly HIP_Y = -0.025;

  constructor() {
    this.matBody   = new THREE.MeshLambertMaterial({ color: BABY_BODY.clone() });
    this.matCollar = new THREE.MeshLambertMaterial({ color: BABY_COLLAR.clone() });
    this.matWing   = new THREE.MeshLambertMaterial({ color: BABY_WING.clone() });
    this.matTail   = new THREE.MeshLambertMaterial({ color: BABY_TAIL.clone() });
    this.matFeath  = new THREE.MeshLambertMaterial({ color: BABY_FEATH.clone() });

    const beakMat = new THREE.MeshLambertMaterial({ color: 0xd4a847 });
    const eyeRing = new THREE.MeshBasicMaterial({ color: 0xdd6600 });
    const eyeMat  = new THREE.MeshBasicMaterial({ color: 0xcc2200 });
    const legMat  = new THREE.MeshLambertMaterial({ color: 0xcc5533 });
    const barMat  = new THREE.MeshLambertMaterial({ color: 0xc8d0d8 });

    this.bodyGroup = new THREE.Group();
    this.bodyGroup.position.y = BODY_Y_OFFSET;
    this.group.add(this.bodyGroup);

    // --- Body ---
    const bodyGeo = new THREE.SphereGeometry(0.19, 14, 10);
    bodyGeo.scale(0.82, 0.78, 1.3);
    const body = new THREE.Mesh(bodyGeo, this.matBody);
    body.position.set(0, 0.07, 0);
    body.rotation.x = 0.18;
    body.castShadow = true;
    this.bodyGroup.add(body);

    // --- Iridescent collar ---
    const collarGeo = new THREE.SphereGeometry(0.11, 10, 8);
    collarGeo.scale(1, 0.5, 1);
    const collar = new THREE.Mesh(collarGeo, this.matCollar);
    collar.position.set(0, 0.14, -0.14);
    this.bodyGroup.add(collar);

    // --- Head group (bobs forward/back while walking) ---
    this.headGroup = new THREE.Group();
    this.headGroup.position.set(0, Pigeon.HEAD_REST_Y, Pigeon.HEAD_REST_Z);
    this.headGroup.rotation.x = Pigeon.HEAD_REST_TILT;
    this.bodyGroup.add(this.headGroup);

    const headMesh = new THREE.Mesh(new THREE.SphereGeometry(HEAD_RADIUS, 12, 10), this.matBody);
    headMesh.castShadow = true;
    this.headGroup.add(headMesh);
    this._buildBeak(this.headGroup, beakMat, eyeRing, eyeMat);

    // --- Wings (pivot from shoulder) ---
    this.leftWingPivot  = new THREE.Group();
    this.rightWingPivot = new THREE.Group();
    this.leftWingPivot.position.set(-0.09, 0.07, 0);
    this.rightWingPivot.position.set( 0.09, 0.07, 0);
    this.bodyGroup.add(this.leftWingPivot);
    this.bodyGroup.add(this.rightWingPivot);

    for (const [pivot, sx] of [
      [this.leftWingPivot,  -1],
      [this.rightWingPivot,  1],
    ] as [THREE.Group, number][]) {
      const wingGeo = new THREE.BoxGeometry(0.13, 0.026, 0.25);
      const wing = new THREE.Mesh(wingGeo, this.matWing);
      wing.position.set(sx * 0.065, 0, 0);
      wing.castShadow = true;
      pivot.add(wing);

      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.014, 0.04), barMat);
      bar.position.set(0, 0.012, -0.04);
      wing.add(bar);

      for (let f = 0; f < 4; f++) {
        const feather = new THREE.Mesh(new THREE.BoxGeometry(0.026, 0.013, 0.088), this.matFeath);
        feather.position.set(sx * (0.02 + f * 0.017), -0.007, 0.088 + f * 0.011);
        wing.add(feather);
      }
    }

    // --- Fan tail ---
    const tailGroup = new THREE.Group();
    tailGroup.position.set(0, 0.01, 0.25);
    tailGroup.rotation.x = -0.38;
    this.bodyGroup.add(tailGroup);

    const tailBase = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.02, 0.14), this.matTail);
    tailGroup.add(tailBase);
    for (let i = -2; i <= 2; i++) {
      const f = new THREE.Mesh(new THREE.BoxGeometry(0.026, 0.011, 0.07), i === 0 ? this.matTail : this.matFeath);
      f.position.set(i * 0.032, 0, 0.095 + Math.abs(i) * 0.007);
      f.rotation.y = i * 0.13;
      tailGroup.add(f);
    }

    // --- Legs (pivot from hip so they swing while walking) ---
    this.leftLegPivot  = new THREE.Group();
    this.rightLegPivot = new THREE.Group();
    this.leftLegPivot.position.set(-0.05, Pigeon.HIP_Y, 0.06);
    this.rightLegPivot.position.set( 0.05, Pigeon.HIP_Y, 0.06);
    this.bodyGroup.add(this.leftLegPivot);
    this.bodyGroup.add(this.rightLegPivot);

    for (const pivot of [this.leftLegPivot, this.rightLegPivot]) {
      // Leg cylinder hangs from the pivot origin
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.010, 0.13, 6), legMat);
      leg.position.set(0, -0.065, 0);
      pivot.add(leg);

      // Foot at bottom of leg
      const foot = new THREE.Mesh(new THREE.SphereGeometry(0.022, 6, 4), legMat);
      foot.scale.set(2.2, 0.5, 1.6);
      foot.position.set(0, -0.133, 0.01);
      pivot.add(foot);
    }

    this.bodyGroup.scale.setScalar(BABY_SCALE);
    this.group.position.copy(this.position);

    // --- First-person view beak ---
    // Intended to be parented to the FP camera by the caller so it follows
    // the view without any manual position/rotation tracking. Uses
    // MeshBasicMaterial (self-lit) so it reads clearly no matter what the
    // world lighting is doing.
    this.viewBeak = new THREE.Group();
    this._buildViewBeak(this.viewBeak);
    this.viewBeak.position.set(0, -0.13, -0.28);
    this.viewBeak.rotation.set(0.32, 0, 0);
    this.viewBeak.visible = false;
  }

  /** Current uniform scale of the bodyGroup (grows with level). */
  get bodyScale(): number {
    return this.bodyGroup.scale.x;
  }

  /**
   * Writes into `out` the world position of a point sitting just on top of
   * the pigeon's head — the mount-point for a "GoPro" first-person camera.
   * Uses `yaw` because the pigeon may not have been rendered yet this frame.
   */
  getHeadMount(yaw: number, out: THREE.Vector3): THREE.Vector3 {
    const s    = this.bodyScale;
    const lx   = 0;
    const ly   = BODY_Y_OFFSET + s * HEAD_LOCAL_Y + s * HEAD_RADIUS + 0.02;
    const lz   = s * HEAD_LOCAL_Z;
    const cosY = Math.cos(yaw);
    const sinY = Math.sin(yaw);
    out.set(
      this.position.x + lx * cosY - lz * sinY,
      this.position.y + ly,
      this.position.z + lx * sinY + lz * cosY,
    );
    return out;
  }

  setLevel(level: number): void {
    const t = Math.min(1, (level - 1) / 4);
    this.bodyGroup.scale.setScalar(Math.min(1.0, BABY_SCALE + SCALE_PER_LEVEL * (level - 1)));
    this.airborneHorizontalSpeedMultiplier =
      BASE_AIRBORNE_HORIZONTAL_SPEED_MULTIPLIER + (level - 1) * AIRBORNE_HORIZONTAL_SPEED_MULTIPLIER_PER_LEVEL;
    this.matBody.color.copy(BABY_BODY.clone().lerp(ADULT_BODY, t));
    this.matCollar.color.copy(BABY_COLLAR.clone().lerp(ADULT_COLLAR, t));
    this.matWing.color.copy(BABY_WING.clone().lerp(ADULT_WING, t));
    this.matTail.color.copy(BABY_TAIL.clone().lerp(ADULT_TAIL, t));
    this.matFeath.color.copy(BABY_FEATH.clone().lerp(ADULT_FEATH, t));
  }

  step(dt: number, input: InputFrame, colliders: readonly AABB[]): void {
    const moveSpeed = (input.sprint && this.stamina > 5 ? SPRINT_SPEED : WALK_SPEED) + this.speedBonus;
    const canFlap = input.flap && this.stamina >= FLAP_STAMINA_COST;
    const horizontalSpeedMultiplier =
      this.grounded && !canFlap ? 1 : this.airborneHorizontalSpeedMultiplier;
    const horizontalSpeed = moveSpeed * horizontalSpeedMultiplier;

    const mag = Math.hypot(input.move.x, input.move.z);
    const nx = mag > 0 ? input.move.x / mag : 0;
    const nz = mag > 0 ? input.move.z / mag : 0;

    const cos = Math.cos(input.yaw);
    const sin = Math.sin(input.yaw);
    this.velocity.x = (nx * cos + nz * sin) * horizontalSpeed;
    this.velocity.z = (nz * cos - nx * sin) * horizontalSpeed;

    if (input.peck) this.peckTimer = Pigeon.PECK_DURATION;
    if (this.peckTimer > 0) this.peckTimer = Math.max(0, this.peckTimer - dt);

    if (canFlap) {
      this.velocity.y = FLAP_IMPULSE;
      this.stamina -= FLAP_STAMINA_COST;
      this.grounded = false;
      this.wingPhase = 0;
    }

    this.velocity.y -= GRAVITY * dt;

    this.moveAxis("x", this.velocity.x * dt, colliders);
    this.moveAxis("z", this.velocity.z * dt, colliders);

    // Record whether we were falling before the vertical move so we can
    // distinguish "landed on a collider top face" from "hit a ceiling".
    const wasFalling = this.velocity.y <= 0;
    this.moveAxis("y", this.velocity.y * dt, colliders);

    if (this.position.y <= PIGEON_HEIGHT + 1e-4) {
      // Base ground
      this.position.y = PIGEON_HEIGHT;
      if (this.velocity.y < 0) this.velocity.y = 0;
      this.grounded = true;
    } else if (wasFalling && this.velocity.y === 0) {
      // Downward motion was stopped by a collider — we landed on a rooftop.
      this.grounded = true;
    } else {
      this.grounded = false;
    }

    if (this.grounded && this.stamina < this.staminaMax) {
      this.stamina = Math.min(this.staminaMax, this.stamina + STAMINA_RECOVERY_PER_SECOND * dt);
    }

    this.group.position.copy(this.position);
    this.group.rotation.y = input.yaw;

    const isWalking  = this.grounded && mag > 0.1;
    const isFlapping = !this.grounded;
    this.wingPhase += dt * (isFlapping ? 28 : isWalking ? 6.5 : 1.8);

    // Wings
    const flapAmt = isFlapping
      ? Math.sin(this.wingPhase) * 0.72
      : Math.sin(this.wingPhase) * (isWalking ? 0.1 : 0.06);
    this.leftWingPivot.rotation.z  =  flapAmt;
    this.rightWingPivot.rotation.z = -flapAmt;

    // Head bob + peck animation (peck overrides and adds a sharp forward-down jab)
    const bobCycle = isWalking ? Math.sin(this.wingPhase) : 0;
    const peck = this.peckProgress;
    this.headGroup.position.z = Pigeon.HEAD_REST_Z + bobCycle * 0.065 - peck * 0.12;
    this.headGroup.position.y = Pigeon.HEAD_REST_Y + Math.abs(bobCycle) * 0.022 - peck * 0.09;
    // Head tilts further down on a peck, starting from its baseline forward tilt.
    this.headGroup.rotation.x = Pigeon.HEAD_REST_TILT - peck * 0.55;

    // Leg swing: left and right alternate
    const legSwing = isWalking ? Math.sin(this.wingPhase) * 0.45 : 0;
    this.leftLegPivot.rotation.x  =  legSwing;
    this.rightLegPivot.rotation.x = -legSwing;
    // Slight knee bend on the back-swinging leg for a more natural gait
    this.leftLegPivot.rotation.z  =  Math.abs(legSwing) * 0.08;
    this.rightLegPivot.rotation.z = -Math.abs(legSwing) * 0.08;
  }

  /**
   * Updates the first-person view-beak. Assumes the beak Group is parented
   * to the FP camera so it follows the view automatically; this only drives
   * the peck-jab animation in local-camera space.
   */
  updateViewBeak(visible: boolean, peckAmt = 0): void {
    this.viewBeak.visible = visible;
    if (!visible) return;
    this.viewBeak.position.y = -0.13 - peckAmt * 0.09;
    this.viewBeak.position.z = -0.28 - peckAmt * 0.03;
    this.viewBeak.rotation.x = 0.32 - peckAmt * 0.8;
  }

  /**
   * Builds the world-space (third-person) beak + eyes into `parent`.
   * Cone is pushed forward so most of it protrudes past the head sphere and
   * reads clearly as a beak even at baby scale.
   */
  private _buildBeak(
    parent: THREE.Object3D,
    beakMat: THREE.MeshLambertMaterial,
    eyeRing: THREE.MeshBasicMaterial,
    eyeMat: THREE.MeshBasicMaterial,
  ): void {
    // Beak sits low on the head (base near the "chin") and is long enough
    // that, combined with the head's resting forward tilt, the tip clearly
    // drops outside the head's angular silhouette when viewed from behind.
    const beak = new THREE.Mesh(new THREE.ConeGeometry(0.033, 0.2, 6), beakMat);
    beak.rotation.x = -Math.PI / 2;
    beak.position.set(0, -0.08, -0.16);
    parent.add(beak);

    const cere = new THREE.Mesh(new THREE.SphereGeometry(0.028, 8, 6), beakMat);
    cere.scale.set(1.7, 0.7, 1);
    cere.position.set(0, -0.03, -0.08);
    parent.add(cere);

    for (const sx of [-1, 1] as const) {
      const ring = new THREE.Mesh(new THREE.SphereGeometry(0.028, 8, 8), eyeRing);
      ring.position.set(0.078 * sx, 0.02, -0.05);
      parent.add(ring);
      const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.016, 6, 6), eyeMat);
      pupil.position.set(0.084 * sx, 0.02, -0.062);
      parent.add(pupil);
    }
  }

  /**
   * Builds the beak geometry used as an overlay in front of the FP camera.
   * Uses `MeshBasicMaterial` so lighting direction / shadows can never
   * wash it out, and a chunky scale so it reads clearly in the view.
   */
  private _buildViewBeak(parent: THREE.Object3D): void {
    const s       = 1.0;
    const beakMat = new THREE.MeshBasicMaterial({ color: 0xd4a847 });
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xdd6600 });
    const pupMat  = new THREE.MeshBasicMaterial({ color: 0x1a0f0a });

    const beak = new THREE.Mesh(new THREE.ConeGeometry(0.05 * s, 0.22 * s, 8), beakMat);
    beak.rotation.x = -Math.PI / 2;
    beak.position.set(0, 0, -0.11 * s);
    parent.add(beak);

    const cere = new THREE.Mesh(new THREE.SphereGeometry(0.04 * s, 8, 6), beakMat);
    cere.scale.set(1.4, 0.7, 1);
    cere.position.set(0, 0.035 * s, 0.015 * s);
    parent.add(cere);

    const forehead = new THREE.Mesh(new THREE.SphereGeometry(0.08 * s, 10, 8), beakMat);
    forehead.material = new THREE.MeshBasicMaterial({ color: 0x9fa9b6 });
    forehead.scale.set(1.1, 0.55, 0.9);
    forehead.position.set(0, 0.045 * s, 0.08 * s);
    parent.add(forehead);

    for (const sx of [-1, 1] as const) {
      const ring = new THREE.Mesh(new THREE.SphereGeometry(0.022 * s, 8, 8), ringMat);
      ring.position.set(0.062 * sx, 0.035 * s, 0.07 * s);
      parent.add(ring);
      const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.012 * s, 6, 6), pupMat);
      pupil.position.set(0.068 * sx, 0.035 * s, 0.06 * s);
      parent.add(pupil);
    }
  }

  private moveAxis(axis: "x" | "y" | "z", delta: number, colliders: readonly AABB[]): void {
    if (delta === 0) return;
    const proposed = this.position[axis] + delta;
    const prev     = this.position[axis];
    this.position[axis] = proposed;

    const cx = this.position.x, cy = this.position.y, cz = this.position.z;
    const r = PIGEON_RADIUS;
    const pigeonMin = { x: cx - r, y: cy - PIGEON_HEIGHT, z: cz - r };
    const pigeonMax = { x: cx + r, y: cy + r,              z: cz + r };

    for (const box of colliders) {
      if (
        pigeonMin.x < box.max.x && pigeonMax.x > box.min.x &&
        pigeonMin.y < box.max.y && pigeonMax.y > box.min.y &&
        pigeonMin.z < box.max.z && pigeonMax.z > box.min.z
      ) {
        this.position[axis] = prev;
        if (axis === "y") this.velocity.y = 0;
        else this.velocity[axis] = 0;
        return;
      }
    }
  }
}
